// AGC: FarmProHub Analytics Page
// AGC: Charts, metrics, and historical data

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { BarChart3, TrendingUp, Users, Coins, Calendar, ArrowUp, ArrowDown } from 'lucide-react'

export const AnalyticsPage: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')

  // AGC: TODO: Fetch real analytics data from backend
  const stats = {
    totalStaked: '0 FP',
    stakedChange: '+0%',
    totalRevenue: '$0.00',
    revenueChange: '+0%',
    totalStakers: 0,
    stakersChange: '+0',
    avgStake: '0 FP',
  }

  // AGC: Mock chart data (will be replaced with real data)
  const chartPlaceholder = Array(12).fill(0).map((_, i) => ({
    label: `${i + 1}`,
    value: Math.random() * 100,
  }))

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4"
        >
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <BarChart3 className="w-7 h-7 text-cyan-400" />
            Analytics
          </h1>
          <p className="text-slate-400 text-sm">
            Track platform metrics and historical data
          </p>
        </motion.div>

        {/* AGC: Time Range Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center"
        >
          <div className="flex bg-slate-800/50 rounded-lg p-1 border border-slate-700/50">
            {(['7d', '30d', '90d', 'all'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-cyan-600 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {range === 'all' ? 'All Time' : range.toUpperCase()}
              </button>
            ))}
          </div>
        </motion.div>

        {/* AGC: Key Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Coins className="w-4 h-4" />
              Total Staked
            </div>
            <p className="text-xl font-bold text-white">{stats.totalStaked}</p>
            <p className="text-xs text-[#0072CE] flex items-center gap-1 mt-1">
              <ArrowUp className="w-3 h-3" />
              {stats.stakedChange}
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <TrendingUp className="w-4 h-4" />
              Total Revenue
            </div>
            <p className="text-xl font-bold text-white">{stats.totalRevenue}</p>
            <p className="text-xs text-[#0072CE] flex items-center gap-1 mt-1">
              <ArrowUp className="w-3 h-3" />
              {stats.revenueChange}
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Users className="w-4 h-4" />
              Total Stakers
            </div>
            <p className="text-xl font-bold text-white">{stats.totalStakers}</p>
            <p className="text-xs text-[#0072CE] flex items-center gap-1 mt-1">
              <ArrowUp className="w-3 h-3" />
              {stats.stakersChange}
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
              <Calendar className="w-4 h-4" />
              Avg Stake
            </div>
            <p className="text-xl font-bold text-white">{stats.avgStake}</p>
          </div>
        </motion.div>

        {/* AGC: Staking Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#0072CE]" />
            Staking Over Time
          </h3>
          
          {/* AGC: Simple Bar Chart Placeholder */}
          <div className="h-48 flex items-end justify-between gap-1">
            {chartPlaceholder.map((bar, index) => (
              <div
                key={index}
                className="flex-1 bg-gradient-to-t from-[#005a9e] to-[#0072CE] rounded-t"
                style={{ height: `${bar.value}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-slate-500">Start</span>
            <span className="text-xs text-slate-500">End</span>
          </div>
          
          <p className="text-center text-slate-500 text-sm mt-4">
            Chart data will be populated when staking activity begins
          </p>
        </motion.div>

        {/* AGC: Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#0072CE]" />
            Revenue Over Time
          </h3>
          
          {/* AGC: Simple Bar Chart Placeholder */}
          <div className="h-48 flex items-end justify-between gap-1">
            {chartPlaceholder.map((bar, index) => (
              <div
                key={index}
                className="flex-1 bg-gradient-to-t from-[#005a9e] to-[#0072CE] rounded-t"
                style={{ height: `${bar.value}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-slate-500">Start</span>
            <span className="text-xs text-slate-500">End</span>
          </div>
          
          <p className="text-center text-slate-500 text-sm mt-4">
            Revenue data will be displayed as projects generate income
          </p>
        </motion.div>

        {/* AGC: Project Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-4">Revenue by Project</h3>
          
          <div className="space-y-3">
            {/* AGC: Origami Battles */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-300 flex items-center gap-2">
                  <span>🎴</span>
                  Origami Battles
                </span>
                <span className="text-sm text-white">100%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full w-full bg-gradient-to-r from-purple-500 to-pink-500" />
              </div>
            </div>
            
            {/* AGC: Coming Soon Projects */}
            <div className="pt-3 border-t border-slate-700/50">
              <p className="text-slate-500 text-sm text-center">
                More projects will be added to the revenue breakdown
              </p>
            </div>
          </div>
        </motion.div>

        {/* AGC: Historical Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-4">Platform Milestones</h3>
          
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
              <span className="text-slate-400">Platform Launch</span>
              <span className="text-slate-300">Coming Soon</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
              <span className="text-slate-400">First Staker</span>
              <span className="text-slate-300">-</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
              <span className="text-slate-400">First Revenue Distribution</span>
              <span className="text-slate-300">-</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-400">First Proposal Passed</span>
              <span className="text-slate-300">-</span>
            </div>
          </div>
        </motion.div>
      </div>
    </main>
  )
}

